import { useT } from '../i18n';
import {
  STATUT_TEREN_VALUES,
  ZONARE_VALUES,
  STARE_TEREN_VALUES,
  SITUATIE_JURIDICA_VALUES,
  UTILITATI_VALUES,
  DRUM_VALUES,
  type ParcelDetails,
  type SelectionMethod,
  type SubmissionFiles,
  type StatutTeren,
  type Zonare,
  type StareTeren,
  type SituatieJuridica,
  type Utilitate,
  type Drum,
} from '../domain/types';
import type { Errors } from '../lib/validation';
import { Field } from './Field';
import { FileInput } from './FileInput';
import './StepForm.css';

interface StepDetailsProps {
  details: ParcelDetails;
  files: SubmissionFiles;
  errors: Errors;
  selectionMethod: SelectionMethod;
  onDetailsChange: (update: Partial<ParcelDetails>) => void;
  onAddCertificate: (files: File[]) => void;
  onRemoveCertificate: () => void;
  onAddAdditional: (files: File[]) => void;
  onRemoveAdditional: (id: string) => void;
}

export function StepDetails({
  details,
  files,
  errors,
  selectionMethod,
  onDetailsChange,
  onAddCertificate,
  onRemoveCertificate,
  onAddAdditional,
  onRemoveAdditional,
}: StepDetailsProps) {
  const t = useT();

  const statutLabels: Record<StatutTeren, string> = {
    intravilan: t.step2.statutOptions.intravilan,
    extravilan: t.step2.statutOptions.extravilan,
    agricol: t.step2.statutOptions.agricol,
    nustiu: t.step2.statutOptions.nustiu,
  };
  const zonareLabels: Record<Zonare, string> = {
    rezidential: t.step2.zonareOptions.rezidential,
    mixt: t.step2.zonareOptions.mixt,
    comercial: t.step2.zonareOptions.comercial,
    altul: t.step2.zonareOptions.altul,
    nustiu: t.step2.zonareOptions.nustiu,
  };

  const stareLabels: Record<StareTeren, string> = {
    liber: t.step2.stareOptions.liber,
    constructie: t.step2.stareOptions.constructie,
    partial: t.step2.stareOptions.partial,
  };

  const situatieLabels: Record<SituatieJuridica, string> = {
    unic: t.step2.situatieOptions.unic,
    coproprietari: t.step2.situatieOptions.coproprietari,
    succesiune: t.step2.situatieOptions.succesiune,
    sarcini: t.step2.situatieOptions.sarcini,
    nustiu: t.step2.situatieOptions.nustiu,
  };

  const utilitatiLabels: Record<Utilitate, string> = {
    curent: t.step2.utilitatiOptions.curent,
    apa: t.step2.utilitatiOptions.apa,
    gaz: t.step2.utilitatiOptions.gaz,
    canal: t.step2.utilitatiOptions.canal,
  };

  const drumLabels: Record<Drum, string> = {
    asfalt: t.step2.drumOptions.asfalt,
    pietruit: t.step2.drumOptions.pietruit,
    pamant: t.step2.drumOptions.pamant,
  };

  const toggleUtilitate = (value: Utilitate) => {
    const has = details.utilitati.includes(value);
    onDetailsChange({
      utilitati: has ? details.utilitati.filter((u) => u !== value) : [...details.utilitati, value],
    });
  };

  return (
    <div className="step-form step-form--wide">
      <header className="step-form__intro">
        <h2>{t.step2.heading}</h2>
        <p className="lede">{t.step2.lede}</p>
      </header>

      <div className="step-form__grid" id="step2-fields">
        <Field
          name="areaSqm"
          label={t.step2.areaLabel}
          help={selectionMethod === 'draw' ? t.step2.areaHelpAuto : t.step2.areaHelpManual}
          error={errors['areaSqm']}
        >
          {(props) => (
            <input
              {...props}
              className="input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={details.areaSqm}
              onChange={(e) => onDetailsChange({ areaSqm: e.target.value })}
            />
          )}
        </Field>

        {/*
          Județ and locality used to sit here. They now live on step 1, beside the map
          that resolves them — asking again two screens later made the owner repeat what
          they had just pinned. This step is about the land's regime, not its position.
        */}

        {/*
          Each grid row is a real pair, and the rows run from the parcel itself outward:

            suprafață          | categoria terenului
            POT                | CUT
            situația juridică  | ce se poate construi
            stare actuală      | acces auto
            utilități

          Category leads because it decides more than anything else on this step —
          28,7% of all variation in price per square metre, measured across 21.212
          listings. The two certificate numbers pair off, then the two questions about
          what the land legally IS, then the two about its physical condition.
        */}
        <Field name="statutTeren" label={t.step2.statutLabel} help={t.step2.statutHelp} error={errors['statutTeren']}>
          {(props) => (
            <select
              {...props}
              className="select"
              value={details.statutTeren}
              onChange={(e) => onDetailsChange({ statutTeren: e.target.value as StatutTeren | '' })}
            >
              <option value="">{t.step2.statutPlaceholder}</option>
              {STATUT_TEREN_VALUES.map((value) => (
                <option key={value} value={value}>
                  {statutLabels[value]}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field name="pot" label={t.step2.potLabel} help={t.step2.potHelp} error={errors['pot']}>
          {(props) => (
            <div className="input-suffix">
              <input
                {...props}
                className="input"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder={t.step2.potPlaceholder}
                value={details.pot}
                onChange={(e) => onDetailsChange({ pot: e.target.value })}
              />
              <span className="input-suffix__unit" aria-hidden="true">
                {t.step2.potSuffix}
              </span>
            </div>
          )}
        </Field>

        <Field name="cut" label={t.step2.cutLabel} help={t.step2.cutHelp} error={errors['cut']}>
          {(props) => (
            <input
              {...props}
              className="input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder={t.step2.cutPlaceholder}
              value={details.cut}
              onChange={(e) => onDetailsChange({ cut: e.target.value })}
            />
          )}
        </Field>

        {/*
          Legal status pairs with zoning: both ask what the land IS, in law and in the
          plan, rather than what it measures.
        */}
        <Field
          name="situatieJuridica"
          label={t.step2.situatieLabel}
          help={t.step2.situatieHelp}
          error={errors['situatieJuridica']}
        >
          {(props) => (
            <select
              {...props}
              className="select"
              value={details.situatieJuridica}
              onChange={(e) =>
                onDetailsChange({ situatieJuridica: e.target.value as SituatieJuridica | '' })
              }
            >
              <option value="">{t.step2.situatiePlaceholder}</option>
              {SITUATIE_JURIDICA_VALUES.map((value) => (
                <option key={value} value={value}>
                  {situatieLabels[value]}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field name="zonare" label={t.step2.zonareLabel} help={t.step2.zonareHelp} error={errors['zonare']}>
          {(props) => (
            <select
              {...props}
              className="select"
              value={details.zonare}
              onChange={(e) => onDetailsChange({ zonare: e.target.value as Zonare | '' })}
            >
              <option value="">{t.step2.zonarePlaceholder}</option>
              {ZONARE_VALUES.map((value) => (
                <option key={value} value={value}>
                  {zonareLabels[value]}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field name="stareTeren" label={t.step2.stareLabel} error={errors['stareTeren']}>
          {(props) => (
            <select
              {...props}
              className="select"
              value={details.stareTeren}
              onChange={(e) => onDetailsChange({ stareTeren: e.target.value as StareTeren | '' })}
            >
              <option value="">{t.step2.starePlaceholder}</option>
              {STARE_TEREN_VALUES.map((value) => (
                <option key={value} value={value}>
                  {stareLabels[value]}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field name="drum" label={t.step2.drumLabel} help={t.step2.drumHelp} error={errors['drum']}>
          {(props) => (
            <select
              {...props}
              className="select"
              value={details.drum}
              onChange={(e) => onDetailsChange({ drum: e.target.value as Drum | '' })}
            >
              <option value="">{t.step2.drumPlaceholder}</option>
              {DRUM_VALUES.map((value) => (
                <option key={value} value={value}>
                  {drumLabels[value]}
                </option>
              ))}
            </select>
          )}
        </Field>

        {/*
          Infrastructure — the two questions the form never asked. Utilities is a
          multi-select (each one present adds to the infrastructure score); road
          access is a single choice. Both optional, like the rest of step 2.
        */}
        <div className="field field--group" role="group" aria-labelledby="utilitati-label">
          <span className="field__label" id="utilitati-label">
            {t.step2.utilitatiLabel}
          </span>
          <div className="check-grid">
            {UTILITATI_VALUES.map((value) => (
              <label className="check-inline" key={value}>
                <input
                  type="checkbox"
                  checked={details.utilitati.includes(value)}
                  onChange={() => toggleUtilitate(value)}
                />
                <span>{utilitatiLabels[value]}</span>
              </label>
            ))}
          </div>
          <p className="field__help">{t.step2.utilitatiHelp}</p>
        </div>
        {details.zonare === 'altul' && (
          <Field name="zonareAlt" label={t.step2.zonareAltLabel} error={errors['zonareAlt']}>
            {(props) => (
              <input
                {...props}
                className="input"
                type="text"
                placeholder={t.step2.zonareAltPlaceholder}
                value={details.zonareAlt}
                onChange={(e) => onDetailsChange({ zonareAlt: e.target.value })}
              />
            )}
          </Field>
        )}
      </div>

      <div className="step-form__stack">
        <FileInput
          name="certificate"
          label={t.step2.certificateLabel}
          help={t.step2.certificateHelp}
          cta={t.step2.certificateCta}
          error={errors['certificate']}
          files={files.certificate ? [files.certificate] : []}
          onAdd={onAddCertificate}
          onRemove={onRemoveCertificate}
        />

        <FileInput
          name="additional"
          label={t.step2.additionalLabel}
          help={t.step2.additionalHelp}
          cta={t.step2.additionalCta}
          error={errors['additional']}
          multiple
          optional
          files={files.additional}
          onAdd={onAddAdditional}
          onRemove={onRemoveAdditional}
        />

        <Field name="intentieNote" label={t.step2.noteLabel} help={t.step2.noteHelp}>
          {(props) => (
            <textarea
              {...props}
              className="textarea"
              rows={4}
              placeholder={t.step2.notePlaceholder}
              value={details.intentieNote}
              onChange={(e) => onDetailsChange({ intentieNote: e.target.value })}
            />
          )}
        </Field>
      </div>
    </div>
  );
}
